import React, { useState } from 'react';
import {
  User, Mail, Phone, MapPin, Calendar, Shield, 
  Camera, Edit2, Save, X, CheckCircle, AlertCircle,
  Globe, Briefcase, Clock, Smartphone, Lock, Key,
  Eye, EyeOff, Upload, Trash2, Plus, ChevronRight,
  Award, Crown, Star, Heart, Share2, Settings,
  Building, DollarSign, TrendingUp, Users, Package, Bell
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PartnerProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  // Profile Data
  const [profile, setProfile] = useState({
    name: 'Partner User',
    email: 'partner@paudhawala.com',
    phone: '+91 99558 59697',
    role: 'Platform Partner',
    department: 'Partnership',
    location: 'Hajipur, Bihar, India',
    bio: 'Strategic partner managing platform operations and seller relationships.',
    joinDate: 'Jan 15, 2024',
    lastActive: 'Today, 10:30 AM',
    avatar: 'P',
    company: 'PaudhaWala Partners',
    position: 'Senior Partner',
    revenue: 520200,
    sellersManaged: 156,
    ordersProcessed: 1245,
    growthRate: 17
  });

  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    smsAlerts: false,
    platformUpdates: true,
    revenueReports: true,
    sellerAlerts: true,
    orderAlerts: true
  });

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setIsEditing(false);
      alert('Profile updated successfully!');
    }, 1500);
  };

  const handleToggleNotification = (key) => {
    setNotifications({ ...notifications, [key]: !notifications[key] });
  };

  const handleChangePassword = () => {
    alert('Password changed successfully!');
    setShowChangePassword(false);
  };

  const stats = [
    { label: 'Revenue Generated', value: `₹${profile.revenue.toLocaleString()}`, icon: DollarSign, color: '#10b981' },
    { label: 'Sellers Managed', value: profile.sellersManaged, icon: Users, color: '#3b82f6' },
    { label: 'Orders Processed', value: profile.ordersProcessed, icon: Package, color: '#8b5cf6' },
    { label: 'Growth Rate', value: `${profile.growthRate}%`, icon: TrendingUp, color: '#f59e0b' },
  ];

  const recentActivities = [
    { action: 'Reviewed seller application', time: '2 hours ago', type: 'seller' },
    { action: 'Generated monthly revenue report', time: '5 hours ago', type: 'report' },
    { action: 'Approved payout request', time: '1 day ago', type: 'payout' },
    { action: 'Updated partnership terms', time: '2 days ago', type: 'settings' },
  ];

  const StatCard = ({ label, value, icon: Icon, color }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: `${color}15` }}>
        <Icon size={18} color={color} />
      </div>
      <div className="stat-info">
        <span className="stat-value">{value}</span>
        <span className="stat-label">{label}</span>
      </div>
    </div>
  );

  return (
    <div className="partner-profile-modern">
      {/* Header */}
      <div className="profile-header">
        <div>
          <h1 className="page-title">Partner Profile</h1>
          <p className="page-subtitle">Manage your account settings and preferences</p>
        </div>
        <div className="header-actions">
          {!isEditing ? (
            <button className="btn-edit" onClick={() => setIsEditing(true)}>
              <Edit2 size={16} />
              Edit Profile
            </button>
          ) : (
            <div className="action-buttons">
              <button className="btn-cancel" onClick={() => setIsEditing(false)}>
                <X size={16} />
                Cancel
              </button>
              <button className="btn-save" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <div className="spinner-small"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Read-Only Banner */}
      <div className="readonly-banner">
        <Eye size={18} />
        <span>You can edit your profile information. Changes will be reflected across the platform.</span>
      </div>

      <div className="profile-layout">
        {/* Sidebar */}
        <div className="profile-sidebar">
          {/* Avatar Section */}
          <div className="avatar-section">
            <div className="avatar-container">
              <div className="avatar">
                <span>{profile.avatar}</span>
              </div>
              {isEditing && (
                <button className="avatar-upload">
                  <Camera size={16} />
                </button>
              )}
            </div>
            <div className="avatar-info">
              <h3>{profile.name}</h3>
              <p>{profile.role}</p>
            </div>
          </div>

          {/* Stats Section */}
          <div className="stats-section">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <StatCard key={idx} {...stat} icon={Icon} />
              );
            })}
          </div>

          {/* Navigation Tabs */}
          <div className="profile-nav">
            <button 
              className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User size={18} />
              Profile Information
            </button>
            <button 
              className={`nav-item ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('notifications')}
            >
              <Bell size={18} />
              Notifications
            </button>
            <button 
              className={`nav-item ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <Lock size={18} />
              Security
            </button>
            <button 
              className={`nav-item ${activeTab === 'activity' ? 'active' : ''}`}
              onClick={() => setActiveTab('activity')}
            >
              <Clock size={18} />
              Activity Log
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="profile-content">
          {/* Profile Information Tab */}
          {activeTab === 'profile' && (
            <div className="content-card">
              <div className="card-header">
                <h3>Profile Information</h3>
                <p>Update your personal information and contact details</p>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>Full Name</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={profile.name}
                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    className="form-input" 
                    value={profile.email}
                    onChange={(e) => setProfile({...profile, email: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input 
                    type="tel" 
                    className="form-input" 
                    value={profile.phone}
                    onChange={(e) => setProfile({...profile, phone: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Company/Organization</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={profile.company}
                    onChange={(e) => setProfile({...profile, company: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Position/Title</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={profile.position}
                    onChange={(e) => setProfile({...profile, position: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={profile.location}
                    onChange={(e) => setProfile({...profile, location: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Bio</label>
                  <textarea 
                    className="form-textarea" 
                    rows="3"
                    value={profile.bio}
                    onChange={(e) => setProfile({...profile, bio: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="info-row">
                  <div className="info-item">
                    <Calendar size={16} />
                    <span>Joined: {profile.joinDate}</span>
                  </div>
                  <div className="info-item">
                    <Clock size={16} />
                    <span>Last Active: {profile.lastActive}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="content-card">
              <div className="card-header">
                <h3>Notification Preferences</h3>
                <p>Choose what notifications you want to receive</p>
              </div>
              <div className="card-body">
                <div className="notification-option">
                  <div>
                    <h4>Email Alerts</h4>
                    <p>Receive notifications via email</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.emailAlerts} 
                      onChange={() => handleToggleNotification('emailAlerts')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="notification-option">
                  <div>
                    <h4>SMS Alerts</h4>
                    <p>Receive notifications via SMS</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.smsAlerts} 
                      onChange={() => handleToggleNotification('smsAlerts')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="notification-option">
                  <div>
                    <h4>Platform Updates</h4>
                    <p>Get notified about platform changes and updates</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.platformUpdates} 
                      onChange={() => handleToggleNotification('platformUpdates')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="notification-option">
                  <div>
                    <h4>Revenue Reports</h4>
                    <p>Receive monthly revenue reports</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.revenueReports} 
                      onChange={() => handleToggleNotification('revenueReports')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="notification-option">
                  <div>
                    <h4>Seller Alerts</h4>
                    <p>Get notified about new seller registrations and activities</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.sellerAlerts} 
                      onChange={() => handleToggleNotification('sellerAlerts')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="notification-option">
                  <div>
                    <h4>Order Alerts</h4>
                    <p>Receive notifications for new orders</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.orderAlerts} 
                      onChange={() => handleToggleNotification('orderAlerts')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="content-card">
              <div className="card-header">
                <h3>Security Settings</h3>
                <p>Manage your password and security preferences</p>
              </div>
              <div className="card-body">
                <div className="security-option">
                  <div>
                    <h4>Change Password</h4>
                    <p>Update your password to keep your account secure</p>
                  </div>
                  <button className="btn-outline" onClick={() => setShowChangePassword(true)}>
                    Change Password
                  </button>
                </div>
                <div className="security-option">
                  <div>
                    <h4>Two-Factor Authentication</h4>
                    <p>Add an extra layer of security to your account</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="security-option">
                  <div>
                    <h4>Session Management</h4>
                    <p>Manage active sessions and devices</p>
                  </div>
                  <button className="btn-outline">Manage Sessions</button>
                </div>
              </div>
            </div>
          )}

          {/* Activity Log Tab */}
          {activeTab === 'activity' && (
            <div className="content-card">
              <div className="card-header">
                <h3>Recent Activity</h3>
                <p>Your recent actions and system activities</p>
              </div>
              <div className="card-body">
                <div className="activity-list">
                  {recentActivities.map((activity, idx) => (
                    <div key={idx} className="activity-item">
                      <div className="activity-icon">
                        <div className={`icon-dot ${activity.type}`}></div>
                      </div>
                      <div className="activity-content">
                        <p>{activity.action}</p>
                        <span>{activity.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="modal-overlay" onClick={() => setShowChangePassword(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Password</h3>
              <button className="modal-close" onClick={() => setShowChangePassword(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Current Password</label>
                <div className="password-input">
                  <input 
                    type={showCurrentPassword ? "text" : "password"} 
                    className="form-input" 
                    placeholder="Enter current password"
                  />
                  <button onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="password-toggle">
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>New Password</label>
                <div className="password-input">
                  <input 
                    type={showNewPassword ? "text" : "password"} 
                    className="form-input" 
                    placeholder="Enter new password"
                  />
                  <button onClick={() => setShowNewPassword(!showNewPassword)} className="password-toggle">
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="password-input">
                  <input 
                    type={showConfirmPassword ? "text" : "password"} 
                    className="form-input" 
                    placeholder="Confirm new password"
                  />
                  <button onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="password-toggle">
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowChangePassword(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleChangePassword}>Change Password</button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .partner-profile-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .profile-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .btn-edit {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          border-radius: 40px;
          color: white;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-edit:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .action-buttons {
          display: flex;
          gap: 12px;
        }

        .btn-save, .btn-cancel {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          border-radius: 40px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }

        .btn-save {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          color: white;
        }

        .btn-save:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-cancel {
          background: white;
          border: 1px solid #e2e8f0;
          color: #64748b;
        }

        .btn-cancel:hover {
          border-color: #ef4444;
          color: #ef4444;
        }

        .spinner-small {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Read-Only Banner */
        .readonly-banner {
          background: #dbeafe;
          border: 1px solid #bfdbfe;
          border-radius: 12px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          color: #1e40af;
          font-size: 13px;
        }

        /* Layout */
        .profile-layout {
          display: grid;
          grid-template-columns: 320px 1fr;
          gap: 24px;
        }

        /* Sidebar */
        .profile-sidebar {
          background: white;
          border-radius: 20px;
          padding: 24px;
          border: 1px solid #e8ecef;
          height: fit-content;
          position: sticky;
          top: 24px;
        }

        .avatar-section {
          text-align: center;
          padding-bottom: 20px;
          border-bottom: 1px solid #e8ecef;
          margin-bottom: 20px;
        }

        .avatar-container {
          position: relative;
          display: inline-block;
        }

        .avatar {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 36px;
          font-weight: 700;
        }

        .avatar-upload {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 32px;
          height: 32px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .avatar-info h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 12px 0 4px;
        }

        .avatar-info p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        /* Stats Section */
        .stats-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 24px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e8ecef;
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .stat-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
        }

        .stat-value {
          display: block;
          font-weight: 700;
          color: #1e293b;
        }

        .stat-label {
          font-size: 11px;
          color: #64748b;
        }

        /* Navigation */
        .profile-nav {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          background: transparent;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .nav-item:hover {
          background: #f8fafc;
          color: #10b981;
        }

        .nav-item.active {
          background: #f0fdf4;
          color: #10b981;
        }

        /* Main Content */
        .profile-content {
          background: white;
          border-radius: 20px;
          border: 1px solid #e8ecef;
          overflow: hidden;
        }

        .content-card {
          padding: 28px;
        }

        .card-header {
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e8ecef;
        }

        .card-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 4px;
        }

        .card-header p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        .card-body {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-size: 13px;
          font-weight: 500;
          color: #1e293b;
        }

        .form-input, .form-textarea {
          width: 100%;
          padding: 10px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          font-size: 13px;
          transition: all 0.3s ease;
        }

        .form-input:focus, .form-textarea:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .form-input:disabled, .form-textarea:disabled {
          background: #f8fafc;
        }

        .info-row {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
          padding-top: 12px;
          border-top: 1px solid #e8ecef;
        }

        .info-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #64748b;
        }

        /* Notification Options */
        .notification-option {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #e8ecef;
        }

        .notification-option:last-child {
          border-bottom: none;
        }

        .notification-option h4 {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 4px;
        }

        .notification-option p {
          font-size: 12px;
          color: #64748b;
          margin: 0;
        }

        /* Toggle Switch */
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 26px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #cbd5e1;
          transition: 0.3s;
          border-radius: 34px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 22px;
          width: 22px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .toggle-slider {
          background-color: #10b981;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(24px);
        }

        /* Security Options */
        .security-option {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          border-bottom: 1px solid #e8ecef;
        }

        .security-option:last-child {
          border-bottom: none;
        }

        .security-option h4 {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 4px;
        }

        .security-option p {
          font-size: 12px;
          color: #64748b;
          margin: 0;
        }

        .btn-outline {
          padding: 6px 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 12px;
          cursor: pointer;
        }

        .btn-outline:hover {
          border-color: #10b981;
          color: #10b981;
        }

        /* Password Input */
        .password-input {
          position: relative;
        }

        .password-input input {
          padding-right: 40px;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #94a3b8;
        }

        /* Activity List */
        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .activity-item {
          display: flex;
          gap: 16px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .icon-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .icon-dot.seller { background: #3b82f6; }
        .icon-dot.report { background: #8b5cf6; }
        .icon-dot.payout { background: #10b981; }
        .icon-dot.settings { background: #f59e0b; }

        .activity-content p {
          font-size: 13px;
          font-weight: 500;
          color: #1e293b;
          margin: 0 0 4px;
        }

        .activity-content span {
          font-size: 11px;
          color: #94a3b8;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .modal-container {
          background: white;
          border-radius: 24px;
          width: 90%;
          max-width: 450px;
          animation: modalSlideIn 0.3s ease;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e8ecef;
        }

        .modal-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .modal-close {
          width: 32px;
          height: 32px;
          background: #f1f5f9;
          border: none;
          border-radius: 8px;
          font-size: 20px;
          cursor: pointer;
        }

        .modal-body {
          padding: 24px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e8ecef;
        }

        .btn-primary, .btn-secondary {
          padding: 8px 20px;
          border-radius: 40px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }

        .btn-primary {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          color: white;
        }

        .btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #64748b;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .profile-layout {
            grid-template-columns: 1fr;
          }
          .profile-sidebar {
            position: static;
          }
        }

        @media (max-width: 768px) {
          .partner-profile-modern {
            padding: 16px;
          }
          .profile-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .action-buttons {
            flex-direction: column;
          }
          .notification-option, .security-option {
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default PartnerProfile;